import { useMemo } from 'react';

import BpkLink from '@skyscanner-internal/backpack-web/bpk-component-link';
import BpkText, { TEXT_STYLES } from '@skyscanner-internal/backpack-web/bpk-component-text';
import reactReplacePlaceholder from 'react-replace-placeholder';

import { COMPONENT_NAME } from '../constants';
import { useVendorCount } from '../hooks/useVendorCount';
import { OPERATIONAL_EVENTS } from '../types';

import type { ConsentBannerStrings, Logger } from '../types';

import STYLES from './BannerContent.module.scss';

export interface BannerContentProps {
  /**
   * Translated UI strings.
   */
  strings: ConsentBannerStrings;
  /**
   * Logger for operational events.
   */
  logger: Logger;
  /**
   * URL for the cookie policy page.
   * @default '/cookiepolicy.aspx'
   */
  cookiePolicyUrl?: string;
}

/**
 * Creates placeholder context for react-replace-placeholder to render links.
 * This matches the pattern used in CookieBanner.
 */
const createLinkContext = (cookiePolicyUrl: string, logger: Logger) => ({
  // Cookie Policy link (tag1/tag2)
  tag1: {
    pair: 'tag2',
    format: (children: React.ReactNode) => (
      <BpkLink
        href={cookiePolicyUrl}
        key="cookiePolicyLink"
        id="cookiePolicyLink"
        onClick={() => {
          logger.logOperationalEvent({
            eventName: OPERATIONAL_EVENTS.COOKIE_POLICY_LINK_CLICKED,
            component: COMPONENT_NAME,
          });
        }}
      >
        {children}
      </BpkLink>
    ),
  },
  // Third party vendors link (tag3/tag4)
  tag3: {
    pair: 'tag4',
    format: (children: React.ReactNode) => (
      <BpkLink
        href="/privacy-settings#vendors"
        key="thirdPartyVendorsLink"
        id="thirdPartyVendorsLink"
        onClick={() => {
          logger.logOperationalEvent({
            eventName: OPERATIONAL_EVENTS.VENDORS_LINK_CLICKED,
            component: COMPONENT_NAME,
          });
        }}
      >
        {children}
      </BpkLink>
    ),
  },
  // Privacy Settings link (tag5/tag6)
  tag5: {
    pair: 'tag6',
    format: (children: React.ReactNode) => (
      <BpkLink
        href="/privacy-settings"
        key="privacySettingsLink"
        id="privacySettingsLink"
        onClick={() => {
          logger.logOperationalEvent({
            eventName: OPERATIONAL_EVENTS.PRIVACY_SETTINGS_LINK_CLICKED,
            component: COMPONENT_NAME,
          });
        }}
      >
        {children}
      </BpkLink>
    ),
  },
});

/**
 * Banner content component displaying title and body text.
 * Note: Parent container handles margin/padding - this component only renders content.
 */
export const BannerContent = ({
  cookiePolicyUrl = '/cookiepolicy.aspx',
  logger,
  strings,
}: BannerContentProps) => {
  const linkContext = useMemo(
    () => createLinkContext(cookiePolicyUrl, logger),
    [cookiePolicyUrl, logger],
  );
  const vendorCount = useVendorCount(logger);

  /**
   * Replaces @@totalNumberOfVendors@@ placeholder with actual vendor count.
   * While loading (null), replaces with empty string to avoid showing "0 partners".
   */
  const replaceVendorCount = (text: string): string =>
    text.replaceAll('@@totalNumberOfVendors@@', vendorCount === null ? '' : String(vendorCount));

  return (
    <>
      <BpkText tagName="h2" textStyle={TEXT_STYLES.heading3} id="consentBannerTitle">
        {strings.title}
      </BpkText>
      {strings.declineAllSectionA && (
        <BpkText tagName="p" textStyle={TEXT_STYLES.sm}>
          {reactReplacePlaceholder(strings.declineAllSectionA, linkContext)}
        </BpkText>
      )}
      {strings.declineAllSectionB && (
        <BpkText
          tagName="p"
          textStyle={TEXT_STYLES.sm}
          className={STYLES['banner-content__section']}
        >
          {reactReplacePlaceholder(replaceVendorCount(strings.declineAllSectionB), linkContext)}
        </BpkText>
      )}
      {strings.declineAllSectionC && (
        <BpkText
          tagName="p"
          textStyle={TEXT_STYLES.sm}
          className={STYLES['banner-content__section']}
        >
          {reactReplacePlaceholder(strings.declineAllSectionC, linkContext)}
        </BpkText>
      )}
    </>
  );
};
